import { useEffect, useRef, useState } from 'react';
import { pagesPubliquesApi } from '../../api/resources';
import { CLE_PHOTO_DEPUIS_LA_PAGE } from '../../lib/lien-public';
import { tr, trf } from '../../lib/i18n';
import { activeLaTraduction } from '../../lib/traduction-ecran';

/** Écart du doigt, en pixels, qui passe à la photo voisine. */
const GLISSE = 50;

/**
 * Une photo de l'offre ou de la facture, en grand : le client la touche dans
 * le PDF et elle s'ouvre ici, en taille réelle.
 *
 * <p>« Imaginons on clique sur la photo, est-ce que elle peut prendre la
 * taille réelle ? » Chaque photo du PDF mène à `/p/<jeton>/photo/<n>`. La
 * page montre la n-ième photo sur fond noir ; deux doigts l'agrandissent,
 * glisser passe à la suivante, ⬇ l'enregistre.
 *
 * @param {{jeton: string, numero: number}} props jeton du lien de la pièce et
 *   rang de la photo, à partir de 1
 * @returns {import('react').ReactElement} la photo en grand
 */
export function PhotoEnGrand({ jeton, numero }) {
  const [piece, setPiece] = useState(null);
  const [introuvable, setIntrouvable] = useState(false);
  const [n, setN] = useState(numero);
  const depart = useRef(null);

  useEffect(() => {
    let annule = false;
    Promise.resolve()
      .then(() => pagesPubliquesApi.images(jeton))
      .then((p) => {
        if (annule) return;
        if (!p || !Array.isArray(p.photos) || !p.photos.length) {
          setIntrouvable(true);
          return;
        }
        setPiece(p);
        setN((actuel) => Math.min(Math.max(1, actuel), p.photos.length));
      })
      .catch(() => {
        if (!annule) setIntrouvable(true);
      });
    return () => {
      annule = true;
    };
  }, [jeton]);

  // La page parle la langue de l'entreprise, comme ses documents.
  const langue = piece?.langue || null;
  useEffect(() => {
    if (langue) activeLaTraduction(langue).catch(() => {});
  }, [langue]);

  // Deux doigts pour agrandir : l'application bloque le zoom de l'écran, la
  // photo le rend le temps de la regarder.
  useEffect(() => {
    const balise = document.querySelector('meta[name="viewport"]');
    if (!balise) return undefined;
    const avant = balise.getAttribute('content');
    balise.setAttribute(
      'content',
      'width=device-width, initial-scale=1.0, maximum-scale=6, viewport-fit=cover',
    );
    return () => balise.setAttribute('content', avant);
  }, []);

  const total = piece?.photos?.length || 0;
  const titre = piece
    ? [piece.nomPiece, piece.numero ? `n° ${piece.numero}` : ''].join(' ').trim()
    : '';

  useEffect(() => {
    if (titre) document.title = titre;
  }, [titre]);

  const va = (suivant) => {
    if (!total || suivant < 1 || suivant > total) return;
    setN(suivant);
    try {
      window.history.replaceState(null, '', `/p/${encodeURIComponent(jeton)}/photo/${suivant}`);
    } catch {
      /* l'adresse ne suit pas : la photo, si */
    }
  };

  const agrandie = () => {
    const vue = typeof window !== 'undefined' ? window.visualViewport : null;
    return !!vue && vue.scale > 1.05;
  };
  const touche = (e) => {
    if (e.touches.length !== 1 || agrandie()) {
      depart.current = null;
      return;
    }
    depart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const lache = (e) => {
    const d = depart.current;
    depart.current = null;
    if (!d || agrandie() || !e.changedTouches.length) return;
    const dx = e.changedTouches[0].clientX - d.x;
    const dy = e.changedTouches[0].clientY - d.y;
    if (Math.abs(dx) < GLISSE || Math.abs(dx) < Math.abs(dy)) return;
    va(dx < 0 ? n + 1 : n - 1);
  };

  // Venue de la page du document : on y revient. Ouverte depuis le PDF (un
  // autre onglet, une autre application), elle mène au document en ligne
  // plutôt qu'à une page vide.
  const ferme = () => {
    let depuisLaPage = false;
    try {
      // La page note qu'elle a ouvert la photo : le lien de la feuille ne
      // transmet pas toujours d'où l'on vient (referrer vide sur iPhone).
      depuisLaPage = window.sessionStorage.getItem(CLE_PHOTO_DEPUIS_LA_PAGE) === jeton;
      window.sessionStorage.removeItem(CLE_PHOTO_DEPUIS_LA_PAGE);
      depuisLaPage =
        depuisLaPage ||
        (!!document.referrer && new URL(document.referrer).origin === window.location.origin);
    } catch {
      depuisLaPage = false;
    }
    if (depuisLaPage && window.history.length > 1) {
      window.history.back();
      return;
    }
    // Sans page derrière : la page du document remplace la photo, sans
    // s'empiler — « Retour » ne rouvre jamais la photo.
    window.location.replace(`/p/${encodeURIComponent(jeton)}`);
  };

  const page = {
    position: 'fixed',
    inset: 0,
    background: '#000',
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: '-apple-system, Segoe UI, Helvetica, Arial, sans-serif',
  };
  const bouton = {
    background: 'rgba(255,255,255,.16)',
    color: '#fff',
    border: 'none',
    borderRadius: 20,
    padding: '8px 14px',
    fontSize: 15,
    fontWeight: 700,
    textDecoration: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
  };

  if (introuvable) {
    return (
      <div style={{ ...page, alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div role="alert" style={{ textAlign: 'center', maxWidth: 340 }}>
          <div style={{ fontSize: 34, marginBottom: 8 }}>🖼</div>
          <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 8 }}>
            Cette photo ne s’ouvre plus
          </div>
          <div style={{ fontSize: 14, color: '#ccc', lineHeight: 1.5 }}>
            Le document n’est plus disponible à cette adresse. Demandez-en un nouveau à l’entreprise
            qui vous l’a envoyé.
          </div>
        </div>
      </div>
    );
  }

  const adresse = pagesPubliquesApi.adresseDeLImage(jeton, n);
  const photo = piece?.photos?.[n - 1];
  const legende = [photo?.legende, photo?.nature]
    .map((t) => String(t || '').trim())
    .filter(Boolean)
    .filter((t, i, tout) => i === 0 || !tout[0].toLowerCase().startsWith(t.toLowerCase()))
    .join(' · ');
  const nomFichier =
    [piece?.nomPiece, piece?.numero, 'photo', n]
      .filter(Boolean)
      .join('-')
      .replace(/[^\p{L}\p{N}-]+/gu, '-') || 'photo';

  return (
    <div style={page}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: 'calc(env(safe-area-inset-top, 0px) + 12px) 14px 10px',
          gap: 10,
        }}
      >
        <button type="button" style={bouton} onClick={ferme}>
          ✕ Fermer
        </button>
        <span style={{ fontSize: 15, fontWeight: 700 }} aria-live="polite">
          {total ? trf('Photo {0} / {1}', n, total) : ''}
        </span>
        <a
          style={bouton}
          href={adresse}
          download={nomFichier}
          aria-label={tr('Enregistrer la photo')}
        >
          ⬇︎
        </a>
      </div>
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
        onTouchStart={touche}
        onTouchEnd={lache}
      >
        {piece && (
          <img
            src={adresse}
            alt={legende || trf('Photo {0}', n)}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
          />
        )}
        {total > 1 && n > 1 && (
          <button
            type="button"
            aria-label={tr('Photo précédente')}
            onClick={() => va(n - 1)}
            style={{
              ...bouton,
              position: 'absolute',
              left: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: 22,
            }}
          >
            ‹
          </button>
        )}
        {total > 1 && n < total && (
          <button
            type="button"
            aria-label={tr('Photo suivante')}
            onClick={() => va(n + 1)}
            style={{
              ...bouton,
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: 22,
            }}
          >
            ›
          </button>
        )}
      </div>
      <div
        style={{
          color: '#ddd',
          textAlign: 'center',
          fontSize: 13,
          lineHeight: 1.5,
          padding: '12px 10px calc(env(safe-area-inset-bottom, 0px) + 18px)',
        }}
      >
        {titre && <div style={{ color: '#fff', fontWeight: 800 }}>{titre}</div>}
        {legende && <div>{legende}</div>}
        <div>Deux doigts pour agrandir · glisser pour la photo suivante</div>
      </div>
    </div>
  );
}
